import React, { startTransition, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch, LayoutTemplate } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  AreaChart,
  Area
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAnalyticsData } from '../store/useAnalyticsStore';
import { useExecutionList } from '../modules/execution/hooks/useExecutionList';
import { useGestaoKPIs } from '../modules/gestao/hooks/useGestaoKPIs';
import { useGestaoTasks } from '../modules/gestao/hooks/useGestaoTasks';
import { useProjectStore } from '../store/useProjectStore';
import { useMyTasks } from '../modules/minha-mesa/hooks/useMyTasks';
import type { TaskFilters } from '../modules/shared/types/task';
import { useOperationalContext } from '../contexts/OperationalContext';
import OperationalMetricsBar from '../components/operational/OperationalMetricsBar';
import { FlowStageAnalysisPanel } from '../components/operational/FlowStageAnalysisPanel';

import GainsMonitorService from '../services/gainsMonitorService';
import {
  getOperationalSlaTrend,
  getOperationalThroughputTrend,
} from '../services/workflowService';
import {
  getLeadTimeTrend,
  getFinancialImpactTrend,
  getStageWipHeatmap,
  type DailyTrendPoint,
  type StageWipRow,
  type FinancialImpactPoint,
} from '../services/capacidadeService';

const BASE_TASK_FILTERS: TaskFilters = {
  status: ['pending', 'in_progress'],
  priority: [],
  search: '',
  overdue: false,
};


const MODEL_ALL = '__all__';
const PERIOD_OPTIONS = [7, 14, 30] as const;
const SECTION_CLASS_NAME = 'rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-elevated)]';
const SECTION_HEADER_CLASS_NAME = 'flex items-center justify-between gap-3 border-b border-[var(--border-default)] px-3 py-2';
const SECTION_TITLE_CLASS_NAME = 'text-[13px] uppercase tracking-[0.06em] text-[var(--text-secondary)]';
const CHART_PANEL_CLASS_NAME = 'rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-3';
const MINI_METRIC_CARD_CLASS_NAME = 'rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-3 py-3';
const TECHNICAL_ACTION_CLASS_NAME = 'inline-flex items-center rounded-[4px] border border-[var(--border-default)] bg-[var(--button-bg)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
const TECHNICAL_INPUT_CLASS_NAME = 'h-8 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 text-[12px] text-[var(--text-primary)] outline-none transition-colors focus:border-[#60A5FA]';
const SEGMENTED_CONTROL_CLASS_NAME = 'inline-flex h-7 items-center rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-elevated)] p-0.5';
const SEGMENTED_OPTION_CLASS_NAME = 'rounded-[3px] px-3 py-1 text-[11px] text-[var(--text-secondary)] transition-colors';
const SECONDARY_ACTION_CLASS_NAME = 'inline-flex h-7 items-center gap-1.5 rounded-[4px] border border-[var(--border-default)] bg-[var(--button-bg)] px-3 text-[11px] font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
// Premium Industrial High-Fidelity Colors
const EMERALD_MAIN = '#10B981'; // Ganhos
const AMBER_MAIN = '#F59E0B'; // SLA em risco / Gargalos
const INDIGO_MAIN = '#6366F1'; // Volume / Throughput
const SLATE_MAIN = '#94A3B8'; // Baseline
const CHART_GRID = 'var(--border-default)';
const CHART_TICK = 'var(--text-secondary)';
const ACCENT_BLUE = INDIGO_MAIN;
const ACCENT_BLUE_SOFT = '#818CF8';
const CHART_BAR = INDIGO_MAIN;
const CHART_ALERT = AMBER_MAIN;
const TABLE_HEAD_CLASS_NAME = 'border-b border-[var(--border-default)] bg-[var(--bg-surface)] text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]';
const TABLE_HEAD_CELL_CLASS_NAME = 'px-2.5 py-1 text-left';
const TABLE_BODY_CELL_CLASS_NAME = 'px-2.5 py-1.5';

function buildDaySeries(days: number): Array<{ date: string; label: string }> {
  const labels: Array<{ date: string; label: string }> = [];
  const formatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  const today = new Date();

  for (let index = days - 1; index >= 0; index -= 1) {
    const current = new Date(today);
    current.setHours(0, 0, 0, 0);
    current.setDate(current.getDate() - index);
    labels.push({
      date: current.toISOString().slice(0, 10),
      label: formatter.format(current),
    });
  }

  return labels;
}

function formatMinutes(minutes?: number): string {
  if (!minutes || minutes <= 0) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatLeadTimeHours(minutes?: number): string {
  if (!minutes || minutes <= 0) return '0h';
  return `${Math.round(minutes / 60)}h`;
}

function formatPercent(value?: number | null): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

function normalizeModelLabel(value?: string | null): string {
  return value && value.trim().length > 0 ? value.trim() : 'Sem versao';
}

function getExecutionProgress(elapsedMinutes?: number | null, expectedMinutes?: number | null, status?: string): number {
  const elapsed = elapsedMinutes ?? 0;
  const expected = expectedMinutes ?? 0;

  if (expected > 0) {
    return Math.max(0, Math.min(100, (elapsed / expected) * 100));
  }

  return status === 'completed' ? 100 : 45;
}

function isWithinPeriod(dateValue?: string, days: number = 7): boolean {
  if (!dateValue) return true;
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return true;

  const threshold = new Date();
  threshold.setHours(0, 0, 0, 0);
  threshold.setDate(threshold.getDate() - (days - 1));
  return parsed >= threshold;
}

function getHeatmapTone(index: number, total: number): string {
  if (total <= 1) return '#37526f';
  const ratio = index / (total - 1);
  if (ratio >= 0.8) return '#6b8cad';
  if (ratio >= 0.55) return '#567492';
  if (ratio >= 0.3) return '#425d79';
  return '#31475f';
}

function getTrendDirection(current: number, previous: number): { arrow: string; label: string } {
  if (current > previous * 1.03) return { arrow: '↑', label: 'Alta' };
  if (current < previous * 0.97) return { arrow: '↓', label: 'Queda' };
  return { arrow: '→', label: 'Estavel' };
}

function PremiumTooltip({
  active,
  payload,
  label,
  formatter,
}: {
  active?: boolean;
  payload?: Array<{ value?: number; payload?: Record<string, unknown> }>;
  label?: string;
  formatter?: (value: number, payload?: Record<string, unknown>) => { main: string; sub: string } | string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0];
  const rawValue = typeof point.value === 'number' ? point.value : 0;
  const formatted = formatter ? formatter(rawValue, point.payload) : { main: String(rawValue), sub: '' };
  const mainText = typeof formatted === 'string' ? formatted : formatted.main;
  const subText = typeof formatted === 'string' ? '' : formatted.sub;

  return (
    <div className="rounded-[6px] border border-[var(--border-default)] bg-[#0F172A] px-4 py-3 text-[11px] shadow-lg backdrop-blur-md">
      <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 mb-1">{label}</div>
      <div className="text-[14px] font-bold text-white mb-0.5">{mainText}</div>
      {subText && <div className="text-[10px] text-slate-400">{subText}</div>}
    </div>
  );
}

function EmptyChartState({ label, onAction, actionLabel }: { label: string; onAction?: () => void; actionLabel?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 gap-1.5">
      <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{label}</span>
      {onAction && actionLabel ? (
        <button type="button" onClick={onAction} className="text-[10px] font-medium text-[#60A5FA] hover:underline">
          {actionLabel} →
        </button>
      ) : (
        <span className="text-[9px] text-[var(--text-muted)]">Inicie execuções para gerar dados</span>
      )}
    </div>
  );
}

export const OperacoesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const userId = user?.id;
  const userOrganizationId = user?.organization_id ?? user?.organizationId;
  const organizationId = workspace?.organizationId ?? userOrganizationId;
  const [selectedPeriodDays, setSelectedPeriodDays] = useState<(typeof PERIOD_OPTIONS)[number]>(7);
  const [selectedModel, setSelectedModel] = useState(MODEL_ALL);
  const [wallboardMode, setWallboardMode] = useState(false);

  const currentProjectId = useProjectStore(state => state.currentProjectId);

  const { historical } = useAnalyticsData();

  const {
    activeFlowId,
    setActiveFlow,
    setActiveExecution,
    setOperationalMetrics,
  } = useOperationalContext();

  const executionQuery = useExecutionList({
    organizationId,
    filters: {
      status: ['active', 'on_hold'],
      search: '',
      overdue: false,
      priority: [],
    },
    page: 1,
    pageSize: 120,
    sortField: 'started_at',
    sortDirection: 'desc',
  });

  const { metrics, usersPerformance, stagePerformance } = useGestaoKPIs({ organizationId });

  const taskQuery = useGestaoTasks({
    organizationId,
    filters: BASE_TASK_FILTERS,
    sortField: 'sla_deadline',
    sortDirection: 'asc',
    page: 1,
    pageSize: 150,
  });

  const { tasks: myTasks, refetch: refetchMyTasks } = useMyTasks({ userId, organizationId });

  const gainsQuery = useQuery({
    queryKey: ['operacoes', 'gains', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => GainsMonitorService.getDashboardData(organizationId!),
    staleTime: 30_000,
  });

  const slaTrendQuery = useQuery({
    queryKey: ['operacoes', 'sla-trend', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => getOperationalSlaTrend(organizationId!, 30),
    staleTime: 60_000,
  });

  const throughput30dQuery = useQuery({
    queryKey: ['operacoes', 'throughput-trend', organizationId, 30],
    enabled: Boolean(organizationId),
    queryFn: () => getOperationalThroughputTrend(organizationId!, 30),
    staleTime: 60_000,
  });

  const leadTimeTrendQuery = useQuery<DailyTrendPoint[]>({
    queryKey: ['operacoes', 'lead-time-trend', organizationId, selectedPeriodDays],
    enabled: Boolean(organizationId),
    queryFn: () => getLeadTimeTrend(organizationId!, selectedPeriodDays),
    staleTime: 60_000,
  });

  const financialImpactQuery = useQuery<FinancialImpactPoint[]>({
    queryKey: ['operacoes', 'financial-impact', organizationId, selectedPeriodDays],
    enabled: Boolean(organizationId),
    queryFn: () => getFinancialImpactTrend(organizationId!, selectedPeriodDays),
    staleTime: 60_000,
  });

  const stageWipQuery = useQuery<StageWipRow[]>({
    queryKey: ['operacoes', 'stage-wip-heatmap', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => getStageWipHeatmap(organizationId!),
    staleTime: 60_000,
  });

  const modelOptions = useMemo(() => {
    const versions = new Set<string>();
    executionQuery.items.forEach((item) => versions.add(normalizeModelLabel(item.templateVersionSemver)));
    return [MODEL_ALL, ...Array.from(versions).sort((left, right) => left.localeCompare(right))];
  }, [executionQuery.items]);

  const filteredExecutionPool = useMemo(() => (
    executionQuery.items.filter((item) => {
      const matchesModel = selectedModel === MODEL_ALL
        ? true
        : normalizeModelLabel(item.templateVersionSemver) === selectedModel;
      const matchesPeriod = isWithinPeriod(item.startedAt ?? item.completedAt, selectedPeriodDays);
      return matchesModel && matchesPeriod;
    })
  ), [executionQuery.items, selectedModel, selectedPeriodDays]);

  const flows = useMemo(() => {
    const grouped = new Map<string, {
      id: string;
      name: string;
      executions: typeof filteredExecutionPool;
    }>();

    filteredExecutionPool.forEach((item) => {
      const flowId = item.templateId ?? 'fluxo-sem-template';
      const flowName = item.templateName ?? 'Fluxo sem template';
      const existing = grouped.get(flowId);
      if (existing) {
        existing.executions.push(item);
      } else {
        grouped.set(flowId, {
          id: flowId,
          name: flowName,
          executions: [item],
        });
      }
    });

    return Array.from(grouped.values())
      .map((flow) => {
        const onTime = flow.executions.filter((item) => !item.isOverdue).length;
        const slaAverage = flow.executions.length > 0 ? (onTime / flow.executions.length) * 100 : 0;
        const bottleneck = flow.executions.find((item) => item.isOverdue)?.currentStageName
          ?? metrics?.most_critical_stage
          ?? stagePerformance[0]?.stage_name
          ?? 'Nenhum';

        return {
          id: flow.id,
          name: flow.name,
          activeExecutions: flow.executions.length,
          slaAverage,
          bottleneck,
        };
      })
      .sort((left, right) => right.activeExecutions - left.activeExecutions);
  }, [filteredExecutionPool, metrics?.most_critical_stage, stagePerformance]);

  useEffect(() => {
    if (activeFlowId && !flows.some((flow) => flow.id === activeFlowId)) {
      setActiveFlow(null, null);
    }
  }, [activeFlowId, flows, setActiveFlow]);

  const filteredExecutions = useMemo(() => {
    if (!activeFlowId) return filteredExecutionPool;
    return filteredExecutionPool.filter((item) => item.templateId === activeFlowId);
  }, [activeFlowId, filteredExecutionPool]);

  const teamPendingCount = taskQuery.items.filter((task) => task.status === 'pending').length;
  const teamInProgressCount = taskQuery.items.filter((task) => task.status === 'in_progress').length;
  const teamOverdueCount = taskQuery.items.filter((task) => task.isOverdue).length;

  const avgLeadTimeMinutes = useMemo(() => {
    const withElapsed = filteredExecutions.filter((item) => typeof item.elapsedMinutes === 'number');
    if (withElapsed.length === 0) return 0;
    return withElapsed.reduce((acc, item) => acc + (item.elapsedMinutes ?? 0), 0) / withElapsed.length;
  }, [filteredExecutions]);

  const currentFlowLabel = useMemo(() => {
    if (!activeFlowId) return 'Todos';
    return flows.find((flow) => flow.id === activeFlowId)?.name ?? 'Todos';
  }, [activeFlowId, flows]);

  const wipStageRows = useMemo(() => (
    stagePerformance
      .filter((stage) => stage.avg_wip > 0)
      .sort((left, right) => right.avg_wip - left.avg_wip)
      .slice(0, 5)
  ), [stagePerformance]);

  const stageRows = useMemo(() => stagePerformance.slice(0, 6), [stagePerformance]);
  const maxStageWip = useMemo(() => Math.max(...wipStageRows.map((stage) => stage.avg_wip), 1), [wipStageRows]);
  const maxImpactValue = useMemo(
    () => Math.max(...stageRows.map((stage) => Math.max(stage.avg_wip, 1) * Math.max(100 - stage.sla_rate, 1)), 1),
    [stageRows],
  );
  const hasSlaTrendData = (slaTrendQuery.data?.length ?? 0) > 0;
  const hasThroughput30dData = (throughput30dQuery.data?.length ?? 0) > 0;
  const hasWipStageData = wipStageRows.length > 0;

  const leadTimeHistoryData = useMemo(() => {
    const buckets = new Map<string, { totalMinutes: number; count: number }>();

    executionQuery.items
      .filter((item) => {
        const matchesModel = selectedModel === MODEL_ALL
          ? true
          : normalizeModelLabel(item.templateVersionSemver) === selectedModel;
        const matchesFlow = activeFlowId ? item.templateId === activeFlowId : true;
        const matchesWindow = isWithinPeriod(item.startedAt ?? item.completedAt, 30);
        return matchesModel && matchesFlow && matchesWindow;
      })
      .forEach((item) => {
        const sourceDate = item.startedAt ?? item.completedAt;
        if (!sourceDate) return;
        const dateKey = String(sourceDate).slice(0, 10);
        const current = buckets.get(dateKey) ?? { totalMinutes: 0, count: 0 };
        current.totalMinutes += item.elapsedMinutes ?? 0;
        current.count += 1;
        buckets.set(dateKey, current);
      });

    return buildDaySeries(30).map(({ date, label }) => {
      const bucket = buckets.get(date);
      const avgMinutes = bucket && bucket.count > 0 ? bucket.totalMinutes / bucket.count : 0;
      return {
        date,
        label,
        value: Math.round(avgMinutes / 60),
        total: bucket?.count ?? 0,
      };
    });
  }, [activeFlowId, executionQuery.items, selectedModel]);

  const recurringBottlenecks = useMemo(() => (
    stagePerformance
      .map((stage) => ({
        ...stage,
        recurrence_score: Math.round((stage.avg_wip * Math.max(100 - stage.sla_rate, 1)) * 10) / 10,
      }))
      .sort((left, right) => right.recurrence_score - left.recurrence_score)
      .slice(0, 3)
  ), [stagePerformance]);

  const maxRecurringScore = useMemo(
    () => Math.max(...recurringBottlenecks.map((stage) => stage.recurrence_score), 1),
    [recurringBottlenecks],
  );

  const financialSeries = useMemo(() => {
    const series = throughput30dQuery.data ?? [];
    const totalSavings = gainsQuery.data?.total_cost_savings ?? 0;
    const totalThroughput = series.reduce((acc, point) => acc + point.value, 0);
    let cumulativeSavings = 0;

    return series.map((point) => {
      const distributedSavings = totalThroughput > 0
        ? (totalSavings * point.value) / totalThroughput
        : 0;
      cumulativeSavings += distributedSavings;
      return {
        ...point,
        cumulativeSavings: Math.round(cumulativeSavings),
        ratio: point.value > 0 ? distributedSavings / point.value : 0,
      };
    });
  }, [gainsQuery.data?.total_cost_savings, throughput30dQuery.data]);

  const financialTrend = useMemo(() => {
    const series = financialSeries;
    if (series.length < 14) return { arrow: '→', label: 'Estavel' };

    const previous = series.slice(-14, -7).reduce((acc, point) => acc + point.cumulativeSavings, 0);
    const current = series.slice(-7).reduce((acc, point) => acc + point.cumulativeSavings, 0);
    return getTrendDirection(current, previous);
  }, [financialSeries]);

  const savingsPerExecution = useMemo(() => {
    const totalThroughput = throughput30dQuery.data?.reduce((acc, point) => acc + point.value, 0) ?? 0;
    if (totalThroughput <= 0) return 0;
    return (gainsQuery.data?.total_cost_savings ?? 0) / totalThroughput;
  }, [gainsQuery.data?.total_cost_savings, throughput30dQuery.data]);
  const hasLeadTimeHistoryData = leadTimeHistoryData.some((point) => (point.total ?? 0) > 0);
  const hasRecurringBottleneckData = recurringBottlenecks.length > 0;
  const hasFinancialSeriesData = financialSeries.some((point) => point.cumulativeSavings > 0 || point.value > 0);
  const overdueExecutions = useMemo(
    () => filteredExecutions.filter((item) => item.isOverdue),
    [filteredExecutions],
  );
  const slaRiskExecutions = useMemo(
    () => filteredExecutions.filter((item) => {
      if (item.isOverdue) return false;
      const expected = Number(item.metadata?.expectedDurationMinutes ?? 0);
      if (expected <= 0) return false;
      const remaining = expected - (item.elapsedMinutes ?? 0);
      return remaining <= 60;
    }),
    [filteredExecutions],
  );
  const systemStatus = useMemo(() => {
    const sla = metrics?.sla_rate_7d ?? 0;
    const bottleneckWip = stageRows[0]?.avg_wip ?? 0;
    const overdueCount = overdueExecutions.length;

    if (sla < 90 || overdueCount >= 3 || bottleneckWip >= 20) {
      return {
        color: 'bg-[#DC2626]',
        label: 'Problema',
        headline: 'Operação com desvio crítico',
        description: 'SLA pressionado, WIP elevado ou gargalo recorrente exige intervenção imediata.',
      };
    }

    if (sla < 97 || overdueCount > 0 || bottleneckWip >= 10) {
      return {
        color: 'bg-[#D97706]',
        label: 'Atencao',
        headline: 'Operação sob monitoramento',
        description: 'Há sinais de pressão operacional, mas o fluxo ainda está controlado.',
      };
    }

    return {
      color: 'bg-[#16A34A]',
      label: 'Operação normal',
      headline: 'Fluxo dentro do regime esperado',
      description: 'SLA, WIP e gargalos estao dentro da faixa operacional prevista.',
    };
  }, [metrics?.sla_rate_7d, overdueExecutions.length, stageRows]);
  const activeProblems = useMemo(() => {
    const cards: Array<{ title: string; flow: string; detail: string; emphasis: string }> = [];
    const primaryBottleneck = stageRows[0];
    const overdueExecution = overdueExecutions[0];
    const riskExecution = slaRiskExecutions[0];

    if (primaryBottleneck) {
      cards.push({
        title: 'Gargalo detectado',
        flow: currentFlowLabel,
        detail: `Etapa: ${primaryBottleneck.stage_name}`,
        emphasis: `WIP: ${primaryBottleneck.avg_wip}`,
      });
    }

    if (overdueExecution) {
      cards.push({
        title: 'Execução atrasada',
        flow: overdueExecution.templateName ?? currentFlowLabel,
        detail: `Codigo: ${overdueExecution.referenceCode ?? overdueExecution.id.slice(0, 8)}`,
        emphasis: `Etapa: ${overdueExecution.currentStageName ?? 'Sem etapa'}`,
      });
    }

    if (riskExecution) {
      cards.push({
        title: 'SLA em risco',
        flow: riskExecution.templateName ?? currentFlowLabel,
        detail: `Codigo: ${riskExecution.referenceCode ?? riskExecution.id.slice(0, 8)}`,
        emphasis: `Restante: ${formatMinutes(Math.max(Number(riskExecution.metadata?.expectedDurationMinutes ?? 0) - (riskExecution.elapsedMinutes ?? 0), 0))}`,
      });
    }

    if (cards.length === 0) {
      cards.push({
        title: 'Sem alertas ativos',
        flow: currentFlowLabel,
        detail: 'Não há gargalos ou execuções fora do padrão no momento.',
        emphasis: 'SLA estável',
      });
    }

    return cards;
  }, [currentFlowLabel, overdueExecutions, slaRiskExecutions, stageRows]);
  const operationalStages = useMemo(() => {
    if (stageRows.length > 0) {
      return stageRows.slice(0, 5).map((stage) => ({
        name: stage.stage_name,
        wip: stage.avg_wip,
        cycle: `${stage.avg_cycle_time_hours.toFixed(1)}h`,
      }));
    }

    return [
      { name: 'Recepção', wip: 0, cycle: '0h' },
      { name: 'Análise', wip: 0, cycle: '0h' },
      { name: 'Aprovação', wip: 0, cycle: '0h' },
      { name: 'Execução', wip: 0, cycle: '0h' },
      { name: 'Entrega', wip: 0, cycle: '0h' },
    ];
  }, [stageRows]);
  const operationalExecutions = useMemo(() => (
    [...filteredExecutions]
      .sort((left, right) => {
        const overdueWeight = Number(Boolean(right.isOverdue)) - Number(Boolean(left.isOverdue));
        if (overdueWeight !== 0) return overdueWeight;
        return (right.elapsedMinutes ?? 0) - (left.elapsedMinutes ?? 0);
      })
      .slice(0, 6)
  ), [filteredExecutions]);
  const operationalKpis = useMemo(() => {
    // Compute trend from SLA data (compare first half vs second half of period)
    const slaData = slaTrendQuery.data ?? [];
    let slaTrend: 'up' | 'down' | 'flat' = 'flat';
    if (slaData.length >= 4) {
      const mid = Math.floor(slaData.length / 2);
      const firstHalf = slaData.slice(0, mid);
      const secondHalf = slaData.slice(mid);
      const avgFirst = firstHalf.reduce((s: number, d: any) => s + (d.sla_rate ?? d.value ?? 0), 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((s: number, d: any) => s + (d.sla_rate ?? d.value ?? 0), 0) / secondHalf.length;
      if (avgSecond > avgFirst + 2) slaTrend = 'up';
      else if (avgSecond < avgFirst - 2) slaTrend = 'down';
    }

    // Throughput trend from throughput data
    const tpData = throughput30dQuery.data ?? [];
    let tpTrend: 'up' | 'down' | 'flat' = 'flat';
    if (tpData.length >= 4) {
      const mid = Math.floor(tpData.length / 2);
      const firstHalf = tpData.slice(0, mid);
      const secondHalf = tpData.slice(mid);
      const avgFirst = firstHalf.reduce((s: number, d: any) => s + (d.count ?? d.value ?? 0), 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((s: number, d: any) => s + (d.count ?? d.value ?? 0), 0) / secondHalf.length;
      if (avgSecond > avgFirst + 0.5) tpTrend = 'up';
      else if (avgSecond < avgFirst - 0.5) tpTrend = 'down';
    }

    const arrow = (t: 'up' | 'down' | 'flat') => t === 'up' ? ' ↑' : t === 'down' ? ' ↓' : '';
    const trendColor = (t: 'up' | 'down' | 'flat', upIsGood: boolean) =>
      t === 'flat' ? '' : (t === 'up') === upIsGood ? 'text-emerald-500' : 'text-red-500';

    return [
      {
        label: 'Lead Time',
        value: formatLeadTimeHours(avgLeadTimeMinutes),
        trend: slaTrend === 'up' ? 'down' as const : slaTrend === 'down' ? 'up' as const : 'flat' as const,
        trendColor: trendColor(slaTrend === 'up' ? 'down' : slaTrend === 'down' ? 'up' : 'flat', false),
      },
      {
        label: 'Throughput',
        value: `${metrics?.throughput_7d ?? 0}${arrow(tpTrend)}`,
        trend: tpTrend,
        trendColor: trendColor(tpTrend, true),
      },
      {
        label: 'WIP',
        value: String(myTasks.length),
        trend: 'flat' as const,
        trendColor: myTasks.length > 10 ? 'text-amber-500' : '',
      },
    ];
  }, [avgLeadTimeMinutes, metrics?.throughput_7d, myTasks.length, slaTrendQuery.data, throughput30dQuery.data]);

  useEffect(() => {
    setOperationalMetrics({
      userWip: myTasks.length,
      globalSla: metrics?.sla_rate_7d ?? 0,
      bottleneckStage: metrics?.most_critical_stage ?? stagePerformance[0]?.stage_name ?? null,
      throughput7d: metrics?.throughput_7d ?? 0,
    });
  }, [
    myTasks.length,
    metrics?.sla_rate_7d,
    metrics?.most_critical_stage,
    metrics?.throughput_7d,
    setOperationalMetrics,
    stagePerformance,
  ]);

  useEffect(() => {
    if (!wallboardMode || !organizationId) return undefined;

    const refreshMonitor = () => {
      void executionQuery.refetch();
      void taskQuery.refetch();
      void refetchMyTasks();
      void gainsQuery.refetch();
      void slaTrendQuery.refetch();
      void throughput30dQuery.refetch();
      void queryClient.invalidateQueries({ queryKey: ['gestao', organizationId] });
    };

    const timer = window.setInterval(refreshMonitor, 30_000);
    return () => window.clearInterval(timer);
  }, [
    executionQuery,
    gainsQuery,
    organizationId,
    queryClient,
    refetchMyTasks,
    slaTrendQuery,
    taskQuery,
    throughput30dQuery,
    wallboardMode,
  ]);

  return (
    <div className="h-full w-full overflow-y-auto bg-[var(--bg-app)] dark:bg-[var(--bg-workspace)] text-[var(--text-primary)]">
        <div className="flex w-full flex-col gap-3 px-5 py-2">
        <section className={`${SECTION_CLASS_NAME} px-3 py-2.5`}>
          <div className="grid gap-2 lg:grid-cols-[minmax(220px,1.4fr)_auto_minmax(180px,0.9fr)]">
            <label className="flex min-w-0 flex-col gap-1">
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]"><GitBranch size={10} />Fluxo</span>
              <select
                value={activeFlowId ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  const flow = flows.find((item) => item.id === value);
                  startTransition(() => {
                    setActiveFlow(flow?.id ?? null, flow?.name ?? null);
                  });
                }}
                className={TECHNICAL_INPUT_CLASS_NAME}
              >
                <option value="">Todos</option>
                {flows.map((flow) => (
                  <option key={flow.id} value={flow.id}>
                    {flow.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Periodo</span>
              <div className="flex h-8 items-center rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-0.5">
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => startTransition(() => setSelectedPeriodDays(option))}
                    className={`min-w-[44px] rounded-[3px] px-2 py-1 text-[10px] uppercase tracking-[0.08em] transition-colors ${
                      selectedPeriodDays === option
                        ? 'bg-[var(--button-hover)] text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {option}d
                  </button>
                ))}
              </div>
            </div>

            <label className="flex min-w-0 flex-col gap-1">
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]"><LayoutTemplate size={10} />Modelo</span>
              <select
                value={selectedModel}
                onChange={(event) => startTransition(() => setSelectedModel(event.target.value))}
                className={TECHNICAL_INPUT_CLASS_NAME}
              >
                <option value={MODEL_ALL}>Todos</option>
                {modelOptions.filter((option) => option !== MODEL_ALL).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <>
            <section className={SECTION_CLASS_NAME}>
              <div className={SECTION_HEADER_CLASS_NAME}>
                <div>
                  <h2 className={SECTION_TITLE_CLASS_NAME}>Status do sistema</h2>
                  <p className="mt-0.5 text-[10px] text-[#64748B]">
                    {wallboardMode ? 'Wallboard ativo com auto refresh a cada 30s.' : 'Andon operacional em tempo real.'}
                  </p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#94A3B8]">
                  Visao {wallboardMode ? 'wallboard' : 'padrao'}
                </div>
              </div>
              <div className={`grid gap-3 p-3 ${wallboardMode ? 'xl:grid-cols-[minmax(340px,0.95fr)_minmax(0,1.05fr)]' : 'xl:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.1fr)]'}`}>
                <div className="rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
                  <div className="flex items-start gap-3">
                    <span className={`mt-1 h-3 w-3 rounded-full ${systemStatus.color}`} />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{systemStatus.label}</div>
                      <div className={`${wallboardMode ? 'text-[28px]' : 'text-[22px]'} mt-1 font-semibold text-[var(--text-primary)]`}>
                        {systemStatus.headline}
                      </div>
                      <div className="mt-2 max-w-[56ch] text-[12px] leading-5 text-[#94A3B8]">
                        {systemStatus.description}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`grid gap-3 ${wallboardMode ? 'md:grid-cols-3' : 'md:grid-cols-3'}`}>
                  {operationalKpis.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => {
                        if (item.label === 'WIP') navigate('/minha-mesa');
                        else if (item.label === 'Throughput') navigate('/execucoes');
                        else navigate('/operacoes');
                      }}
                      className="rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-left transition-colors hover:bg-[var(--button-hover)] cursor-pointer"
                    >
                      <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{item.label}</div>
                      <div className={`${wallboardMode ? 'text-[30px]' : 'text-[22px]'} mt-2 font-semibold ${item.trendColor || 'text-[var(--text-primary)]'}`}>
                        {item.value}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className={SECTION_CLASS_NAME}>
              <div className={SECTION_HEADER_CLASS_NAME}>
                <h2 className={SECTION_TITLE_CLASS_NAME}>Problemas ativos</h2>
              </div>
              <div className="grid gap-3 p-3 xl:grid-cols-3">
                {activeProblems.map((problem) => (
                  <button
                    key={`${problem.title}-${problem.detail}`}
                    type="button"
                    onClick={() => {
                      if (problem.title === 'Gargalo detectado') navigate('/operacoes');
                      else if (problem.title === 'Execução atrasada' || problem.title === 'SLA em risco') navigate('/execucoes');
                      else if (problem.title === 'Sem alertas ativos') navigate('/execucoes');
                    }}
                    className="rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-left transition-colors hover:bg-[var(--button-hover)] cursor-pointer"
                  >
                    <div className="text-[10px] uppercase tracking-[0.08em] text-[#F59E0B]">{problem.title}</div>
                    <div className="mt-2 text-[15px] font-semibold text-[var(--text-primary)]">{problem.flow}</div>
                    <div className="mt-1 text-[12px] text-[var(--text-secondary)]">{problem.detail}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--text-primary)]">{problem.emphasis}</span>
                      <span className="text-[10px] text-[var(--text-muted)]">Ver detalhes →</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>

            <section className={SECTION_CLASS_NAME}>
              <div className={SECTION_HEADER_CLASS_NAME}>
                <h2 className={SECTION_TITLE_CLASS_NAME}>Fluxo operacional</h2>
              </div>
              <div className="overflow-x-auto px-3 py-4">
                <div className="flex min-w-max items-center gap-2">
                  {operationalStages.map((stage, index) => (
                    <React.Fragment key={stage.name}>
                      {index > 0 ? <div className="h-px w-6 bg-[var(--border-default)]" /> : null}
                      <div className={`w-[180px] rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] ${wallboardMode ? 'p-4' : 'p-3'}`}>
                        <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Etapa</div>
                        <div className="mt-1 truncate text-[14px] font-semibold text-[var(--text-primary)]">{stage.name}</div>
                        <div className="mt-3 flex items-end justify-between gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">WIP</div>
                            <div className={`${wallboardMode ? 'text-[24px]' : 'text-[18px]'} mt-1 font-semibold text-[#60A5FA]`}>
                              {stage.wip}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Ciclo</div>
                            <div className="mt-1 text-[12px] text-[var(--text-primary)]">{stage.cycle}</div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </section>

            
          </>
        ) : (
          <>
        <OperationalMetricsBar
          variant="ruler"
          slaGlobal={metrics?.sla_rate_7d ?? 0}
          throughput={metrics?.throughput_7d ?? 0}
          wip={myTasks.length}
          bottleneck={metrics?.most_critical_stage ?? stagePerformance[0]?.stage_name ?? null}
          leadTimeLabel={formatLeadTimeHours(avgLeadTimeMinutes)}
        />

        <section className={SECTION_CLASS_NAME}>
          <div className="border-b border-[var(--border-default)] px-3 py-2">
            <div className={SECTION_TITLE_CLASS_NAME}>Leituras estrategicas</div>
          </div>
          <div className="grid gap-3 p-3 xl:grid-cols-2">
          <div className={CHART_PANEL_CLASS_NAME}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <h2 className="text-[12px] font-medium text-[var(--text-primary)]">Tendencia SLA (30d)</h2>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Meta 100%</span>
            </div>
            <div className="h-[150px]">
              {hasSlaTrendData ? (
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={slaTrendQuery.data ?? []} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      interval={4}
                      tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }}
                      tickFormatter={(value) => `${value}%`}
                      width={38}
                    />
                    <ReferenceLine y={100} stroke="#64748B" strokeDasharray="4 4" ifOverflow="extendDomain" />
                    <Tooltip
                      cursor={{ stroke: '#31475f', strokeWidth: 1 }}
                      content={<PremiumTooltip formatter={(value, payload) => ({ main: `${value.toFixed(1)}%`, sub: `${payload?.total || 0} concluídas | ${payload?.late || 0} fora da meta` })} />}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={ACCENT_BLUE}
                      strokeWidth={1.5}
                      dot={(props) => {
                        const pointValue = Number(props.payload?.value ?? 0);
                        if (pointValue >= 100) return <circle cx={props.cx} cy={props.cy} r={1.5} fill={ACCENT_BLUE_SOFT} />;
                        return <circle cx={props.cx} cy={props.cy} r={2.5} fill={CHART_ALERT} stroke="var(--bg-surface)" strokeWidth={1} />;
                      }}
                      activeDot={{ r: 3, fill: ACCENT_BLUE_SOFT, stroke: 'var(--bg-surface)', strokeWidth: 1 }}
                      isAnimationActive={true} animationDuration={1500} animationEasing="ease-out"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados no período" />
              )}
            </div>
          </div>

          <div className={CHART_PANEL_CLASS_NAME}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <h2 className="text-[12px] font-medium text-[var(--text-primary)]">Throughput por dia (30d)</h2>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Execucoes concluidas</span>
            </div>
            <div className="h-[150px]">
              {hasThroughput30dData ? (
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={throughput30dQuery.data ?? []} margin={{ top: 8, right: 6, left: -22, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      interval={4}
                      tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }}
                      width={26}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(95, 127, 162, 0.08)' }}
                      content={<PremiumTooltip formatter={(value) => ({ main: `${value} execuções`, sub: '' })} />}
                    />
                    <Bar dataKey="value" fill={CHART_BAR} radius={[0, 0, 0, 0]} isAnimationActive={true} animationDuration={1500} animationEasing="ease-out" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados no período" />
              )}
            </div>
          </div>

          <div className={CHART_PANEL_CLASS_NAME}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="text-[12px] font-medium text-[var(--text-primary)]">WIP medio por etapa</h2>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Top {Math.max(wipStageRows.length, 1)}</span>
            </div>
            <div className="h-[146px]">
              {hasWipStageData ? (
                <ResponsiveContainer width="100%" height={146}>
                  <BarChart
                    data={wipStageRows}
                    layout="vertical"
                    margin={{ top: 0, right: 20, left: 24, bottom: 0 }}
                  >
                    <CartesianGrid horizontal={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      domain={[0, maxStageWip]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="stage_name"
                      axisLine={false}
                      tickLine={false}
                      width={112}
                      tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(95, 127, 162, 0.08)' }}
                      content={<PremiumTooltip formatter={(value) => ({ main: `${value} itens em WIP`, sub: '' })} />}
                    />
                    <Bar dataKey="avg_wip" fill={ACCENT_BLUE} radius={[0, 0, 0, 0]} isAnimationActive={true} animationDuration={1500} animationEasing="ease-out">
                      <LabelList
                        dataKey="avg_wip"
                        position="right"
                        offset={6}
                        className="fill-[var(--text-secondary)]"
                        fontSize={10}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados no período" />
              )}
            </div>
          </div>
          <div className={CHART_PANEL_CLASS_NAME}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <h2 className="text-[12px] font-medium text-[var(--text-primary)]">Lead time historico</h2>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Base de inicio 30d</span>
            </div>
            <div className="h-[146px]">
              {hasLeadTimeHistoryData ? (
                <ResponsiveContainer width="100%" height={146}>
                  <AreaChart data={leadTimeHistoryData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSla" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} interval={4} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} width={34} tickFormatter={(value) => `${value}h`} />
                    <Tooltip content={<PremiumTooltip formatter={(value, payload) => ({ main: `${value}h lead time`, sub: `${payload?.total || 0} execuções` })} />} />
                    <ReferenceLine y={95} stroke="#EF4444" strokeDasharray="3 3" label={{ position: 'top', fill: '#EF4444', fontSize: 10, value: 'Meta (95h)' }} />
                    <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={3} fillOpacity={1} fill="url(#colorSla)" activeDot={{ r: 4, strokeWidth: 0, fill: '#10B981', style: { filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.8))' } }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados no periodo" />
              )}
            </div>
          </div>

          <div className={`${CHART_PANEL_CLASS_NAME} xl:col-span-2`}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <h2 className="text-[12px] font-medium text-[var(--text-primary)]">Gargalo recorrente (top 3 etapas)</h2>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Indice WIP x perda SLA</span>
            </div>
            <div className="h-[124px]">
              {hasRecurringBottleneckData ? (
                <ResponsiveContainer width="100%" height={124}>
                  <BarChart data={recurringBottlenecks.map(b => ({...b, va: b.recurrence_score * 0.4, nva: b.recurrence_score * 0.6}))} layout="vertical" margin={{ top: 0, right: 18, left: 36, bottom: 0 }}>
                    <CartesianGrid horizontal={false} vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                    <YAxis type="category" dataKey="stage_name" axisLine={false} tickLine={false} width={124} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                    <Tooltip content={<PremiumTooltip formatter={(value, payload) => ({ main: `${value} pts`, sub: `WIP: ${payload?.avg_wip || 0} | SLA: ${Number(payload?.sla_rate || 0).toFixed(1)}%` })} />} />
                    <Bar dataKey="va" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} isAnimationActive={true} animationDuration={1500} />
                    <Bar dataKey="nva" stackId="a" fill="#1E293B" radius={[0, 10, 10, 0]} isAnimationActive={true} animationDuration={1500}>
                      <LabelList dataKey="recurrence_score" position="right" offset={6} className="fill-[var(--text-secondary)]" fontSize={10} formatter={(v: unknown) => Number(v).toFixed(0)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados no periodo" />
              )}
            </div>
          </div>
        </div>
        
        {/* NOVO: Gráfico de Pareto de Motivos de Parada (NVA) */}
        <div className="grid gap-3 px-3 pb-3 xl:grid-cols-1">
          <div className={CHART_PANEL_CLASS_NAME}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <h3 className="text-[12px] font-medium text-[var(--text-primary)]">Motivos de Pausa (Pareto NVA)</h3>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Acumulado do mês</span>
            </div>
            
            <div className="h-[146px]">
              {historical?.pauseReasonPareto && historical.pauseReasonPareto.length > 0 ? (
                <ResponsiveContainer width="100%" height={146}>
                  <ComposedChart
                    data={historical.pauseReasonPareto}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="reason" axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} width={34} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} width={34} tick={{ fill: '#F59E0B', fontSize: 10, fontWeight: 500 }} tickFormatter={(val) => `${val}%`} />
                    <Tooltip content={<PremiumTooltip formatter={(value, payload) => {
                      if (payload?.dataKey === 'count') return { main: `${value} paradas`, sub: `Freq` };
                      return { main: `${Number(value).toFixed(1)}%`, sub: 'Acumulado' };
                    }} />} />
                    <Bar yAxisId="left" dataKey="count" fill="rgba(245, 158, 11, 0.2)" stroke="#F59E0B" strokeWidth={1} radius={[4, 4, 0, 0]} isAnimationActive={true} animationDuration={1500} />
                    <Line yAxisId="right" type="monotone" dataKey="cumulativePercentage" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: '#F59E0B', strokeWidth: 0, style: { filter: 'drop-shadow(0 0 3px rgba(245,158,11,0.5))' } }} activeDot={{ r: 5 }} isAnimationActive={true} animationDuration={1500} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Nenhuma pausa registrada" />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className={SECTION_CLASS_NAME}>
          <div className={SECTION_HEADER_CLASS_NAME}>
            <h2 className={SECTION_TITLE_CLASS_NAME}>Fluxos ativos</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-[11px]">
              <thead className={TABLE_HEAD_CLASS_NAME}>
                <tr>
                  <th className={TABLE_HEAD_CELL_CLASS_NAME}>Fluxo</th>
                  <th className={TABLE_HEAD_CELL_CLASS_NAME}>Execucoes ativas</th>
                  <th className={TABLE_HEAD_CELL_CLASS_NAME}>SLA medio</th>
                  <th className={TABLE_HEAD_CELL_CLASS_NAME}>Gargalo</th>
                </tr>
              </thead>
              <tbody>
                {flows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-2.5 py-3 text-center">
                      <span className="text-[11px] text-[var(--text-secondary)]">Sem fluxos ativos no período</span>
                      <button type="button" onClick={() => navigate('/execucoes')} className="ml-2 text-[11px] font-medium text-[#60A5FA] hover:underline">Abrir execuções →</button>
                    </td>
                  </tr>
                ) : (
                  flows.map((flow) => {
                    const isActive = flow.id === activeFlowId;
                    return (
                      <tr
                        key={flow.id}
                        onClick={() => setActiveFlow(flow.id, flow.name)}
                        className={`cursor-pointer border-b border-[var(--border-default)] hover:bg-[var(--bg-surface)] ${isActive ? 'bg-[var(--bg-surface)]' : ''}`}
                      >
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)] ${isActive ? 'border-l-2 border-l-[#60A5FA] pl-[8px]' : ''}`}>
                          {flow.name}
                        </td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{flow.activeExecutions}</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{formatPercent(flow.slaAverage)}</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-secondary)]`}>{flow.bottleneck}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        {activeFlowId && organizationId && (
          <section className={SECTION_CLASS_NAME}>
            <div className={SECTION_HEADER_CLASS_NAME}>
              <h2 className={SECTION_TITLE_CLASS_NAME}>Análise por etapa</h2>
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">TP · TRA · SLA por etapa</span>
            </div>
            <FlowStageAnalysisPanel
              organizationId={organizationId}
              templateId={activeFlowId}
              flowName={currentFlowLabel}
            />
          </section>
        )}

        <div className="grid grid-cols-12 gap-3">
          <section className={`${SECTION_CLASS_NAME} col-span-12 xl:col-span-8`}>
            <div className={SECTION_HEADER_CLASS_NAME}>
              <div className="min-w-0">
                <h2 className={SECTION_TITLE_CLASS_NAME}>Execucoes em andamento</h2>
                <p className="mt-0.5 text-[10px] text-[var(--text-secondary)]">
                  Fluxo: {currentFlowLabel} | Janela: {selectedPeriodDays}d
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/workflow')}
                className={TECHNICAL_ACTION_CLASS_NAME}
              >
                Modulo de execucoes
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead className={TABLE_HEAD_CLASS_NAME}>
                  <tr>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>Codigo</th>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>Etapa atual</th>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>Tempo decorrido</th>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>SLA restante</th>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>Progresso</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExecutions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-2.5 py-3 text-center">
                        <span className="text-[11px] text-[var(--text-secondary)]">Sem execuções no período</span>
                        <button type="button" onClick={() => navigate('/execucoes')} className="ml-2 text-[11px] font-medium text-[#60A5FA] hover:underline">Abrir execuções →</button>
                      </td>
                    </tr>
                  ) : (
                    filteredExecutions.map((item) => {
                      const elapsed = item.elapsedMinutes ?? 0;
                      const expected = Number(item.metadata?.expectedDurationMinutes ?? 0);
                      const remaining = expected > 0 ? expected - elapsed : null;
                      const progress = getExecutionProgress(elapsed, expected, item.status);

                      return (
                        <tr
                          key={item.id}
                          onClick={() => navigate(`/execucoes/instancias/${encodeURIComponent(item.id)}`)}
                          className="cursor-pointer border-b border-[var(--border-default)] hover:bg-[var(--bg-surface)]"
                        >
                          <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>
                            {item.referenceCode ?? item.title ?? item.id.slice(0, 8)}
                          </td>
                          <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{item.currentStageName ?? 'Sem etapa'}</td>
                          <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{formatMinutes(elapsed)}</td>
                          <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>
                            {remaining === null ? 'N/D' : formatMinutes(Math.max(remaining, 0))}
                          </td>
                          <td className={TABLE_BODY_CELL_CLASS_NAME}>
                            <div className="flex items-center gap-2">
                              <div className="h-[4px] flex-1 bg-[#162130]">
                                <div
                                  className="h-[4px] rounded-[1px] bg-[#5f7fa2]"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="w-10 text-right text-[10px] tracking-[0.08em] text-[var(--text-secondary)]">
                                {formatPercent(progress)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className={`${SECTION_CLASS_NAME} col-span-12 xl:col-span-4`}>
            <div className={SECTION_HEADER_CLASS_NAME}>
              <h2 className={SECTION_TITLE_CLASS_NAME}>Tarefas da equipe</h2>
              <button
                type="button"
                onClick={() => navigate('/minha-mesa')}
                className={TECHNICAL_ACTION_CLASS_NAME}
              >
                Minha mesa
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 border-b border-[var(--border-default)] p-3">
              {[
                { label: 'Pendentes', value: teamPendingCount },
                { label: 'Em execução', value: teamInProgressCount },
                { label: 'Atrasadas', value: teamOverdueCount },
                { label: 'Meu WIP', value: myTasks.length },
              ].map((item) => (
                <div key={item.label} className={MINI_METRIC_CARD_CLASS_NAME}>
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{item.label}</div>
                  <div className="mt-1 text-[16px] font-semibold text-[var(--text-primary)]">{item.value}</div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead className={TABLE_HEAD_CLASS_NAME}>
                  <tr>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>Responsavel</th>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>WIP</th>
                    <th className={TABLE_HEAD_CELL_CLASS_NAME}>SLA 7d</th>
                  </tr>
                </thead>
                <tbody>
                  {usersPerformance.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-2.5 py-2 text-[11px] text-[var(--text-secondary)]">
                        Nenhuma carga distribuida identificada no periodo.
                      </td>
                    </tr>
                  ) : (
                    usersPerformance.slice(0, 5).map((userPerf) => (
                      <tr key={userPerf.user_id} className="border-b border-[var(--border-default)]">
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{userPerf.user_email}</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{userPerf.avg_wip}</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{formatPercent(userPerf.sla_rate_7d)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <section className={SECTION_CLASS_NAME}>
          <div className={SECTION_HEADER_CLASS_NAME}>
            <h2 className={SECTION_TITLE_CLASS_NAME}>Gargalos</h2>
          </div>

          {stageRows.length === 0 ? (
            <div className="px-3 py-3 text-center">
              <span className="text-[12px] text-[var(--text-secondary)]">Nenhum gargalo detectado no período</span>
              <button type="button" onClick={() => navigate('/execucoes')} className="ml-2 text-[11px] font-medium text-[#60A5FA] hover:underline">Abrir execuções →</button>
            </div>
          ) : (
            <>
              <div className="border-b border-[var(--border-default)] px-3 py-3">
                <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Heatmap linear</div>
                <div className="flex h-7 overflow-hidden rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)]">
                  {stageRows.map((stage, index) => {
                    const impactValue = Math.max(stage.avg_wip, 1) * Math.max(100 - stage.sla_rate, 1);
                    const width = `${Math.max((impactValue / maxImpactValue) * 100, 8)}%`;
                    return (
                      <div
                        key={stage.stage_name}
                        title={`${stage.stage_name}: impacto ${(100 - stage.sla_rate).toFixed(1)}%`}
                        className="flex min-w-[48px] items-center justify-center border-r border-[var(--border-subtle)] px-1 text-center text-[9px] text-[var(--text-primary)]"
                        style={{
                          width,
                          backgroundColor: getHeatmapTone(index, stageRows.length),
                        }}
                      >
                        <span className="truncate">{stage.stage_name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead className={TABLE_HEAD_CLASS_NAME}>
                    <tr>
                      <th className={TABLE_HEAD_CELL_CLASS_NAME}>Etapa</th>
                      <th className={TABLE_HEAD_CELL_CLASS_NAME}>WIP</th>
                      <th className={TABLE_HEAD_CELL_CLASS_NAME}>Tempo medio</th>
                      <th className={TABLE_HEAD_CELL_CLASS_NAME}>Impacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stageRows.map((stage) => (
                      <tr key={stage.stage_name} onClick={() => navigate('/execucoes')} className="cursor-pointer border-b border-[var(--border-default)] hover:bg-[var(--bg-surface)]">
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{stage.stage_name}</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{stage.avg_wip}</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{stage.avg_cycle_time_hours.toFixed(1)}h</td>
                        <td className={`${TABLE_BODY_CELL_CLASS_NAME} text-[var(--text-primary)]`}>{formatPercent(100 - stage.sla_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section className={SECTION_CLASS_NAME}>
          <div className={SECTION_HEADER_CLASS_NAME}>
            <h2 className={SECTION_TITLE_CLASS_NAME}>Impacto financeiro</h2>
            <a
              href={`#/ganhos?projectId=${currentProjectId || ''}&stageId=sustentacao`}
              className="text-[11px] font-medium text-[var(--accent-primary)] hover:underline"
            >
              Monitor de Sustentação
            </a>
          </div>
          <div className="grid gap-3 p-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.85fr)]">
            <div className={CHART_PANEL_CLASS_NAME}>
              <div className="mb-1 flex items-center justify-between gap-3">
                <h3 className="text-[12px] font-medium text-[var(--text-primary)]">Economia acumulada (30d)</h3>
                <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">Distribuida por throughput</span>
              </div>
              <div className="h-[146px]">
                {hasFinancialSeriesData ? (
                  <ResponsiveContainer width="100%" height={146}>
                    <AreaChart data={financialSeries} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="emeraldGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={EMERALD_MAIN} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={EMERALD_MAIN} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                      <XAxis dataKey="label" axisLine={false} tickLine={false} interval={4} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} width={72} tickFormatter={(value) => `R$${Math.round(Number(value) / 1000)}k`} />
                      <Tooltip content={<PremiumTooltip formatter={(value, payload) => ({ main: GainsMonitorService.formatCurrency(value), sub: `${payload?.value || 0} execuções` })} />} />
                      <Area type="monotone" dataKey="cumulativeSavings" stroke={EMERALD_MAIN} fill="url(#emeraldGradient)"  strokeWidth={1.5} dot={false} activeDot={{ r: 3, fill: ACCENT_BLUE_SOFT }} isAnimationActive={true} animationDuration={1500} animationEasing="ease-out" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChartState label="Sem dados no periodo" />
                )}
              </div>
            </div>

            <div className={CHART_PANEL_CLASS_NAME}>
              <div className="mb-3 grid grid-cols-2 gap-2">
                {[
                  { label: 'Economia acumulada', value: GainsMonitorService.formatCurrency(gainsQuery.data?.total_cost_savings ?? 0) },
                  { label: 'Lead time medio', value: formatLeadTimeHours(avgLeadTimeMinutes) },
                  { label: 'Throughput 30d', value: throughput30dQuery.data?.reduce((acc, point) => acc + point.value, 0) ?? 0 },
                  { label: 'Tendencia', value: `${financialTrend.arrow} ${financialTrend.label}` },
                ].map((item) => (
                  <div key={item.label} className={MINI_METRIC_CARD_CLASS_NAME}>
                    <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{item.label}</div>
                    <div className="mt-1 text-[14px] font-semibold text-[var(--text-primary)]">{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="mb-1 flex items-center justify-between gap-3">
                <h3 className="text-[12px] font-medium text-[var(--text-primary)]">Economia x throughput</h3>
                <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">R$ por execução</span>
              </div>
              <div className="h-[120px]">
                {hasFinancialSeriesData ? (
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={financialSeries.slice(-10)} margin={{ top: 8, right: 6, left: -12, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                      <XAxis dataKey="label" axisLine={false} tickLine={false} interval={1} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10, fontWeight: 500 }} width={54} tickFormatter={(value) => `R$${Math.round(Number(value))}`} />
                      <Tooltip content={<PremiumTooltip formatter={(value, payload) => ({ main: `R$ ${Number(payload?.ratio || 0).toFixed(2)} por execução`, sub: `${payload?.value || 0} execuções` })} />} />
                      <Bar dataKey="ratio" fill={CHART_BAR} radius={[0, 0, 0, 0]} isAnimationActive={true} animationDuration={1500} animationEasing="ease-out" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChartState label="Sem dados no periodo" />
                )}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">
                Relação atual: {GainsMonitorService.formatCurrency(savingsPerExecution)} por execução concluída.
              </div>
            </div>
          </div>
        </section>
          </>
      </div>

        {/* ── Process Mining — Lead Time Trend ──────────────────────── */}
        <section className={SECTION_CLASS_NAME}>
          <div className={SECTION_HEADER_CLASS_NAME}>
            <h2 className={SECTION_TITLE_CLASS_NAME}>Lead Time por Dia</h2>
            <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">
              Média em horas · Process Mining
            </span>
          </div>
          <div className="px-3 py-3">
            <div className="h-[160px]">
              {(leadTimeTrendQuery.data?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={leadTimeTrendQuery.data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} interval={Math.max(0, Math.floor((leadTimeTrendQuery.data?.length ?? 1) / 7) - 1)} tick={{ fill: CHART_TICK, fontSize: 10 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10 }} width={44} tickFormatter={(v) => `${Math.round(Number(v))}h`} />
                    <Tooltip content={<PremiumTooltip formatter={(v) => ({ main: `${Number(v).toFixed(1)}h`, sub: 'lead time médio' })} />} />
                    <Line type="monotone" dataKey="value" stroke={INDIGO_MAIN} strokeWidth={1.5} dot={false} activeDot={{ r: 3, fill: INDIGO_MAIN }} isAnimationActive />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados de lead time no período" />
              )}
            </div>
          </div>
        </section>

        {/* ── Process Mining — Impacto Financeiro ───────────────────── */}
        <section className={SECTION_CLASS_NAME}>
          <div className={SECTION_HEADER_CLASS_NAME}>
            <h2 className={SECTION_TITLE_CLASS_NAME}>Impacto Financeiro</h2>
            <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">
              Economia vs Custo · R$ por dia
            </span>
          </div>
          <div className="px-3 py-3">
            <div className="h-[160px]">
              {(financialImpactQuery.data?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={financialImpactQuery.data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} interval={Math.max(0, Math.floor((financialImpactQuery.data?.length ?? 1) / 7) - 1)} tick={{ fill: CHART_TICK, fontSize: 10 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10 }} width={54} tickFormatter={(v) => `R$${Math.round(Number(v))}`} />
                    <Tooltip content={<PremiumTooltip formatter={(v, payload) => ({ main: `R$ ${Number(v).toFixed(0)}`, sub: payload ? `custo: R$ ${Number((payload as Record<string, unknown>).custo ?? 0).toFixed(0)}` : '' })} />} />
                    <Bar dataKey="economia" fill={EMERALD_MAIN} radius={[2, 2, 0, 0]} isAnimationActive />
                    <Bar dataKey="custo" fill={CHART_ALERT} radius={[2, 2, 0, 0]} isAnimationActive />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState label="Sem dados financeiros no período" />
              )}
            </div>
          </div>
        </section>

        {/* ── WIP Heatmap por Etapa ─────────────────────────────────── */}
        <section className={SECTION_CLASS_NAME}>
          <div className={SECTION_HEADER_CLASS_NAME}>
            <h2 className={SECTION_TITLE_CLASS_NAME}>WIP por Etapa</h2>
            <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">
              Tarefas ativas vs limite configurado
            </span>
          </div>
          <div className="px-3 py-3 overflow-x-auto">
            {(stageWipQuery.data?.length ?? 0) > 0 ? (
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border-default)]">
                    <th className="text-left py-1 px-2 text-[var(--text-muted)] font-medium">Etapa</th>
                    <th className="text-center py-1 px-2 text-[var(--text-muted)] font-medium">WIP Atual</th>
                    <th className="text-center py-1 px-2 text-[var(--text-muted)] font-medium">Limite</th>
                    <th className="text-center py-1 px-2 text-[var(--text-muted)] font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stageWipQuery.data!.map((row) => (
                    <tr key={row.stageId} className={`border-b border-[var(--border-default)] ${row.overLimit ? 'bg-red-500/5' : ''}`}>
                      <td className="py-1.5 px-2 text-[var(--text-primary)]">{row.stageName}</td>
                      <td className="py-1.5 px-2 text-center font-semibold text-[var(--text-primary)]">{row.wipAtual}</td>
                      <td className="py-1.5 px-2 text-center text-[var(--text-secondary)]">
                        {row.wipLimite != null ? row.wipLimite : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        {row.overLimit ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100/30 text-red-500 border border-red-300/40">Acima do limite</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100/30 text-emerald-600 border border-emerald-300/40">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center h-16 text-[11px] text-[var(--text-muted)]">
                Sem dados de WIP — configure limites nas etapas de workflow.
              </div>
            )}
          </div>
        </section>

    </div>
  );
};
export default OperacoesPage;
